import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
  createFilterOptions
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useMemo, useState } from "react";
import type { ParsedActivityRow } from "./catalogModel";
import { compactFieldSx } from "./formStyles";

type ReusableActivity = {
  id: string;
  title: string;
  description: string | null;
};

type Props = {
  /** Source assessment whose existing activities are reusable. Hides the
   *  picker entirely when undefined. */
  sourceAssessmentId?: string;
  /** Activities under the source assessment. `null` = still loading;
   *  `[]` = source set but assessment has no activities yet. The page
   *  hoists the fetch so the editor can also see the same list. */
  sourceActivities: ReusableActivity[] | null;
  /** True when the fetch for sourceActivities errored. Without this flag the
   *  picker would stay on an indefinite spinner — a `null` array can mean
   *  either "still loading" or "fetch failed and was never retried". */
  sourceActivitiesFailed?: boolean;
  /** Activities already in the current draft for this instrument; used to mark
   *  rows already-added so the user doesn't double-add. */
  currentRows: ParsedActivityRow[];
  /** Append one or more activity rows to the current instrument. Always
   *  takes an array so callers (notably "Add all unused") can apply a single
   *  patch instead of N stomping patches — React batches state updates inside
   *  an event handler, and per-row patches would all be computed from the
   *  render-time activity list and overwrite each other. */
  onAppend: (rows: ParsedActivityRow[]) => void;
};

const filterOptions = createFilterOptions<ReusableActivity>({
  matchFrom: "any",
  stringify: (opt) => `${opt.title} ${opt.description ?? ""}`,
  // Cap visible matches so a stray empty query doesn't drop 7k DOM nodes
  // into the dropdown popper at once. Users still narrow further by typing.
  limit: 100
});

// Searchable typeahead: the active source assessment's activities are loaded
// once and filtered client-side as the user types. Picking a row appends it
// to the current instrument's activities. Categories are *not* surfaced in
// the CSV — the reused activity row already carries its category links in
// the DB.
export function ReusableActivitiesPicker({
  sourceAssessmentId,
  sourceActivities,
  sourceActivitiesFailed,
  currentRows,
  onAppend
}: Props) {
  const activities: ReusableActivity[] = sourceActivities ?? [];
  const failed = Boolean(sourceActivitiesFailed);
  // Treat the fetch as "still loading" only when we have a source set AND no
  // data has arrived AND we haven't recorded a failure. Without the failed
  // check, an errored fetch would keep this picker on the spinner forever.
  const loading =
    sourceAssessmentId !== undefined && sourceActivities === null && !failed;
  const [inputValue, setInputValue] = useState("");

  const existingTitlesLower = useMemo(
    () => new Set(currentRows.map((r) => r.title.trim().toLowerCase())),
    [currentRows]
  );

  const unusedActivities = useMemo(
    () => activities.filter((a) => !existingTitlesLower.has(a.title.trim().toLowerCase())),
    [activities, existingTitlesLower]
  );

  if (!sourceAssessmentId) return null;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Reuse activities from existing assessment
        </Typography>
        {loading ? <CircularProgress size={14} /> : null}
        {!loading && !failed ? (
          <Typography variant="caption" color="text.secondary">
            {activities.length} available · {unusedActivities.length} not yet added
          </Typography>
        ) : null}
      </Stack>
      {failed ? (
        <Alert severity="error" variant="outlined" sx={{ mb: 1 }}>
          Couldn't load activities for this assessment. Change the assessment
          title and pick it again to retry.
        </Alert>
      ) : null}
      {!failed && !loading && activities.length === 0 ? (
        <Alert severity="info" variant="outlined" sx={{ mb: 1 }}>
          No reusable activities under this assessment yet. Add new ones below.
        </Alert>
      ) : null}
      {activities.length > 0 ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Autocomplete
            size="small"
            options={activities}
            loading={loading}
            // Controlled input so we can clear after a pick.
            inputValue={inputValue}
            onInputChange={(_, val, reason) => {
              if (reason !== "reset") setInputValue(val);
            }}
            // We never hold a "selected" value — picking is an action, not a
            // selection. Treat the value as null and re-clear on every change.
            value={null}
            onChange={(_, val) => {
              if (val && typeof val === "object") {
                const alreadyAdded = existingTitlesLower.has(
                  val.title.trim().toLowerCase()
                );
                if (!alreadyAdded) {
                  onAppend([
                    {
                      title: val.title,
                      description: val.description ?? "",
                      // Categories live on the activity row already (linked
                      // via activity_category). No CSV column needed here;
                      // the reused row keeps its DB links on import.
                      category: ""
                    }
                  ]);
                }
                setInputValue("");
              }
            }}
            blurOnSelect
            filterOptions={filterOptions}
            getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.title)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            sx={{ ...compactFieldSx, flex: 1 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search activities"
                placeholder="Type to filter — pick to add"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress size={14} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
            renderOption={(props, opt) => {
              const alreadyAdded = existingTitlesLower.has(
                opt.title.trim().toLowerCase()
              );
              return (
                <Box
                  component="li"
                  {...props}
                  key={opt.id}
                  sx={{
                    opacity: alreadyAdded ? 0.55 : 1,
                    pointerEvents: alreadyAdded ? "none" : "auto"
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ flex: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {opt.title}
                        </Typography>
                        {alreadyAdded ? (
                          <Chip
                            size="small"
                            color="success"
                            icon={<CheckCircleIcon />}
                            label="Added"
                            sx={{ height: 18 }}
                          />
                        ) : null}
                      </Stack>
                      {opt.description ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", whiteSpace: "pre-wrap" }}
                        >
                          {opt.description}
                        </Typography>
                      ) : null}
                    </Box>
                  </Stack>
                </Box>
              );
            }}
          />
          <Tooltip
            title={
              unusedActivities.length === 0
                ? "All available activities are already in this instrument"
                : `Append all ${unusedActivities.length} not-yet-added activities`
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={unusedActivities.length === 0}
                onClick={() => {
                  onAppend(
                    unusedActivities.map((a) => ({
                      title: a.title,
                      description: a.description ?? "",
                      category: ""
                    }))
                  );
                }}
                sx={{ whiteSpace: "nowrap" }}
              >
                Add all unused
              </Button>
            </span>
          </Tooltip>
        </Stack>
      ) : null}
    </Box>
  );
}
